import { LitElement, html } from 'lit';

/**
 * Digital Services Special Terms Content
 * Renders the full legal text for Digital Services special terms.
 * Content matches the official PDF word-for-word.
 */
class DigitalSpecialTerms extends LitElement {
    createRenderRoot() {
        return this;
    }

    render() {
        return html`
            <div class="special-terms">
                <h4 class="text-base font-bold mb-4">DIGITAL SERVICES - SPECIAL TERMS</h4>

                <h5>1. ABOUT THESE SPECIAL TERMS</h5>

                <p>
                    1.1 These Special Terms for Digital Services sets out the Special Terms that
                    apply to Digital Services acquired by the Client through a Booking Confirmation.
                </p>

                <h5>2. DEFINITIONS</h5>

                <p>
                    2.1 For the purposes of these Special Terms, the following terms have the
                    following meanings:
                </p>

                <p class="ml-6">
                    (a) <strong>Digital Services</strong> means the digital advertising and
                    marketing services specified in this Booking Confirmation and/or these Special
                    Terms, as applicable.
                </p>

                <p class="ml-6">
                    (b) <strong>Inventory</strong> means the actual or scheduled impressions or
                    placements of advertising material, shown on publisher platforms, properties,
                    websites, or apps. Inventory may be characterised by the publisher, audience,
                    specifications, and time dimensions that the advertising material is booked,
                    scheduled, or restricted to.
                </p>

                <p class="ml-6">
                    (c) <strong>Rich Media</strong> means any advertising content that utilizes
                    elements of animation, video, in-ad interaction, or uses third party ad servers,
                    or that utilizes HTML or AMP ad formats.
                </p>

                <p>
                    2.2 Terms not defined in these Special Terms have the same meaning as the Terms
                    and Conditions.
                </p>

                <h5>3. DIGITAL SERVICES</h5>

                <p>
                    3.1 The Client acknowledges that all placements detailed in the relevant
                    Statements of Work are non-cancellable.
                </p>

                <p>
                    3.2 All Inventory is subject to availability. REACH will work with the Client to
                    find suitable replacement inventory if desired placements are no longer
                    available.
                </p>

                <p>
                    3.3 No additional targeting (geo, browser, behavioural, etc.), other than that
                    which is explicitly listed on the Booking Confirmation or in these Special
                    Terms, will be undertaken without REACH's prior written consent.
                </p>

                <h5>4. THIRD PARTY VENDORS</h5>

                <p>
                    4.1 The Digital Services will not involve the use or any third party Rich Media
                    vendors unless listed in these Special Terms and approved by REACH. The addition
                    of a third party Rich Media vendor may increase the cost of the Digital
                    Services.
                </p>

                <p>
                    4.2 All other third parties used by the Client must be confirmed by REACH. If
                    approved by REACH, the Client must provide reporting logins to REACH, and the
                    campaign must be associated to REACH in the third party system for automated
                    delivery data ingestion.
                </p>

                <p>
                    4.3 Where the Digital Services includes placements of advertising on third party
                    sites, the terms and conditions of those third party sites shall apply as
                    follows:
                </p>

                <p class="ml-6">
                    (a) placements through Google Ads will be subject to the Google Ads policies
                    found here:
                    <a
                        href="https://support.google.com/adspolicy/answer/6008942?utm_source=awfe&utm_medium=email&utm_campaign=20103244"
                        target="_blank"
                        rel="noopener noreferrer"
                        >https://support.google.com/adspolicy/answer/6008942?utm_source=awfe&amp;utm_medium=email&amp;utm_campaign=20103244</a
                    >
                </p>

                <p class="ml-6">
                    (b) placements through Meta Ads will be subject to the Meta terms and conditions
                    found here:
                    <a
                        href="https://transparency.meta.com/policies/ad-standards"
                        target="_blank"
                        rel="noopener noreferrer"
                        >https://transparency.meta.com/policies/ad-standards</a
                    >
                </p>

                <h5>5. LIABILITY</h5>

                <p>
                    5.1 While REACH will endeavour to use all advertising spend to meet campaign
                    goals, REACH does not guarantee any specific results including but not limited
                    to, brand recognition uplift, store traffic, impression volume, ad click volume,
                    or volume of website sales and conversions.
                </p>

                <p>
                    5.2 REACH shall not be liable in any way if any campaign is removed or suspended
                    by the relevant third party platform or site owner.
                </p>

                <p>
                    5.3 Optimisation towards campaign goals may be dependent on correct
                    implementation of provided pixel or conversion tracking code to the client
                    website. This implementation is the sole responsibility of the Client. REACH
                    shall not be liable for errors in the implementation of conversion tracking.
                </p>

                <h5>6. PRICING</h5>

                <p>
                    6.1 Notwithstanding clause 5.2 of the Terms and Conditions, any and all set up
                    costs will be billed in the first month of distribution.
                </p>

                <p>
                    6.2 Commencing one month after the Effective Date of the Booking Confirmation
                    and notwithstanding clause 5.2 of the Terms and Conditions, REACH will bill the
                    Digital Services in monthly increments based on 1st party delivery data. The
                    Client shall pay each invoice in full unless otherwise agreed by REACH prior to
                    the start date of the campaign. No adjustments to billing methodology may occur
                    after launch.
                </p>

                <p>
                    6.3 REACH will invoice off first party delivered impressions from its ad-servers
                    unless agreed upon in writing prior to campaign launch to use delivery data from
                    an approved third party.
                </p>

                <p>
                    6.4 If REACH fails to deliver any Target Media Spend, REACH will use reasonable
                    endeavours to provide additional inventory to make good delivery. If such
                    inventory is not available to reach the Target Media Spend, REACH will only bill
                    on delivered media.
                </p>
            </div>
        `;
    }
}

customElements.define('digital-special-terms', DigitalSpecialTerms);
